import getLookups from '../../views/Config/lookups'
import { camelCase, capitalCase } from 'change-case';

/**
 * Generates a normalizer function to transform user-defined entries for a Creatable Select component into a standardized format.
 * This higher-order function first checks if the specified form is supported by looking for it in the predefined entries. If the form
 * is supported, it returns a function that normalizes a given entry. If the entry matches one of the predefined entries for the form,
 * the predefined entry is returned. Otherwise, the entry is formatted by converting the label to capital case for readability and
 * the value to camelCase, adhering to backend storage conventions.
 *
 * This method is particularly useful for scenarios where users can type and create new options in a Creatable Select field. It ensures
 * that all entries, whether predefined or custom, are formatted consistently for both display in the UI and storage in the backend.
 *
 * @param {string} form The form identifier, used to validate against predefined entries in the select component. The existence of the form
 * is checked upfront to ensure only supported forms are processed.
 * @returns {Function} A normalizer function that takes an entry object with `label` and `value` properties as its argument. For predefined
 * entries, it returns the corresponding predefined object; for new entries, it returns an object with the label formatted in capital case
 * for display and the value in camelCase for backend compatibility.
 * @throws {Error} Throws an error if the specified form is not supported, indicating that it does not exist in the predefined entries, thus
 * ensuring the form's validity is checked only once during the normalizer generation.
 */
export function generateNormalizer(form) {
    const predifinedEntries = getLookups();
    if (!Object.keys(predifinedEntries).includes(form)) {
        throw new Error(`Form: ${form} is not supported`)
    }
    return (entry) => {
        let predifinedEntry = predifinedEntries[form].find(en => en.label === entry.label && en.value === entry.value)
        return predifinedEntry ? predifinedEntry : { ...entry, label: capitalCase(entry.label), value: camelCase(entry.value) }
    }
}
